import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useLocale } from '../contexts/LocaleContext'
import { siteConfig, getCitySlugWithPrefix } from '../config/site'
import { SITE_NAME } from '../config/siteName'
import { updateSEO } from '../utils/seo'
import { filterActiveCities } from '../utils/activeCities'
import { markdownToHtml } from '../utils/markdownToHtml'
import { getPageSlugs } from '../utils/getSlugs'
import citiesData from '../utils/getCitiesData'
import './Home.css'

const Home = () => {
  const [cities, setCities] = useState([])
  const [filteredCities, setFilteredCities] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [homepageData, setHomepageData] = useState(null)
  const [openAccordions, setOpenAccordions] = useState({
    whyChoose: true,
    howItWorks: false,
    safety: false
  })
  const [openFaqIndex, setOpenFaqIndex] = useState(null)
  const navigate = useNavigate()
  const { t, translations } = useLocale()

  useEffect(() => {
    // Load cities data and filter by active cities
    const activeCities = filterActiveCities(citiesData)
    setCities(activeCities)
    setFilteredCities(activeCities)
  }, [])

  useEffect(() => {
    // Load generated homepage content
    const loadHomepage = async () => {
      try {
        // Try loading from src/data/home.json first
        const homepage = await import(`../data/home.json`)
        setHomepageData(homepage.default || homepage)
      } catch (error) {
        console.log('Generated homepage not found, using locale translations')
        setHomepageData(null)
      }
    }
    loadHomepage()
  }, [])

  useEffect(() => {
    // Update SEO meta tags - use generated content only
    const seoTitle = homepageData?.title 
      ? `${homepageData.title} | ${siteConfig.baseUrl.replace('https://', '')}`
      : `${SITE_NAME} | ${siteConfig.baseUrl.replace('https://', '')}`
    const seoDescription = homepageData?.intro 
      ? homepageData.intro.replace(/\*\*/g, '').replace(/\*/g, '').substring(0, 160)
      : t('seo.homeDescription') // Keep SEO fallback for meta description
    
    updateSEO({
      title: seoTitle,
      description: seoDescription,
      keywords: t('seo.homeKeywords'),
      url: siteConfig.baseUrl
    })
  }, [t, homepageData])

  useEffect(() => {
    // Filter cities based on search query
    if (!searchQuery.trim()) {
      setFilteredCities(cities)
      return
    }

    const filtered = cities.filter((city) =>
      city.City.toLowerCase().includes(searchQuery.toLowerCase()) ||
      city.Slug.toLowerCase().includes(searchQuery.toLowerCase())
    )
    setFilteredCities(filtered)
  }, [searchQuery, cities])

  const handleCityClick = (citySlug) => {
    const fullSlug = getCitySlugWithPrefix(citySlug)
    navigate(`/${fullSlug}`)
  }

  const toggleAccordion = (key) => {
    setOpenAccordions(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index)
  }

  // Sort cities by population (most popular first)
  const sortedCities = [...filteredCities].sort((a, b) => b.Population - a.Population)
  const popularCities = sortedCities.slice(0, 6)

  // Get internal pages from locale
  const internalPages = translations?.home?.internalPages || {}
  const pageSlugs = getPageSlugs(translations)
  
  // Define internal pages to display (in order)
  const internalPageKeys = [
    'rankings', 'free', 'safety', 'discreet', 'speed', 
    'comparisons', 'beginner', 'telegram', 'discord', 
    'socialMedia', 'onlyfans', 'pornTube', 'edgy'
  ]

  // Use generated content only (all content is AI-generated via generate-homepage.py)
  const title = homepageData?.title || ''
  const subtitle = homepageData?.subtitle || ''
  const intro = homepageData?.intro || ''
  const whyChoose = homepageData?.whyChoose || null
  const howItWorks = homepageData?.howItWorks || null
  const safety = homepageData?.safety || null
  const faq = homepageData?.faq || null

  return (
    <div className="home-container">
      {title && (
        <header className="home-header">
          <h1 className="home-title">{title}</h1>
          {subtitle && <p className="home-subtitle">{subtitle}</p>}
        </header>
      )}

      <section className="choose-city-section">
        {/* Hero Image */}
        <div className="city-hero-image-wrapper">
          <img 
            src="/images/city-hero.png" 
            alt="Dating profiles"
            className="city-hero-image"
            loading="eager"
            onError={(e) => {
              // Fallback: hide image if it doesn't exist yet
              e.target.style.display = 'none';
            }}
          />
        </div>
        
        <h2 className="choose-city-title">{t('home.chooseCity')}</h2>
        <p className="choose-city-description">{t('home.chooseCityDescription')}</p>
        
        <div className="search-section">
          <input
            type="text"
            className="city-search-input"
            placeholder={t('home.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </section>

      {searchQuery && (
        <section className="cities-section">
          <h2 className="section-title">{t('home.allCities')}</h2>
          <div className="cities-grid">
            {sortedCities.map((city) => (
              <div
                key={city.Slug}
                className="city-card"
                onClick={() => handleCityClick(city.Slug)}
              >
                <h3 className="city-name">{city.City}</h3>
                <p className="city-population">
                  {t('common.population')}: {city.Population.toLocaleString('de-DE')}
                </p>
              </div>
            ))}
          </div>
          {sortedCities.length === 0 && (
            <p className="no-results">{t('common.noCitiesFound')}</p>
          )}
        </section>
      )}

      {!searchQuery && (
        <>
          {/* SEO Content Section - Only show if we have generated content */}
          {(intro || whyChoose || howItWorks || safety) && (
            <section className="seo-content-section">
              <div className="seo-content">
                {intro && (
                  <div className="seo-intro" dangerouslySetInnerHTML={{ __html: markdownToHtml(intro) }} />
                )}
                
                <div className="accordions-container">
                  {whyChoose && (
                    <div className={`accordion-item ${openAccordions.whyChoose ? 'open' : ''}`}>
                      <button 
                        className="accordion-header"
                        onClick={() => toggleAccordion('whyChoose')}
                        aria-expanded={openAccordions.whyChoose}
                      >
                        <h2 className="accordion-title">{whyChoose.title}</h2>
                        <span className="accordion-icon">{openAccordions.whyChoose ? '−' : '+'}</span>
                      </button>
                      <div className="accordion-content">
                        <div className="accordion-text" dangerouslySetInnerHTML={{ __html: markdownToHtml(whyChoose.content) }} />
                      </div>
                    </div>
                  )}
                  
                  {howItWorks && (
                    <div className={`accordion-item ${openAccordions.howItWorks ? 'open' : ''}`}>
                      <button 
                        className="accordion-header"
                        onClick={() => toggleAccordion('howItWorks')}
                        aria-expanded={openAccordions.howItWorks}
                      >
                        <h2 className="accordion-title">{howItWorks.title}</h2>
                        <span className="accordion-icon">{openAccordions.howItWorks ? '−' : '+'}</span>
                      </button>
                      <div className="accordion-content">
                        <div className="accordion-text" dangerouslySetInnerHTML={{ __html: markdownToHtml(howItWorks.content) }} />
                      </div>
                    </div>
                  )}
                  
                  {safety && (
                    <div className={`accordion-item ${openAccordions.safety ? 'open' : ''}`}>
                      <button 
                        className="accordion-header"
                        onClick={() => toggleAccordion('safety')}
                        aria-expanded={openAccordions.safety}
                      >
                        <h2 className="accordion-title">{safety.title}</h2>
                        <span className="accordion-icon">{openAccordions.safety ? '−' : '+'}</span>
                      </button>
                      <div className="accordion-content">
                        <div className="accordion-text" dangerouslySetInnerHTML={{ __html: markdownToHtml(safety.content) }} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}

          {/* Internal Pages Section */}
          {internalPages && Object.keys(internalPages).length > 0 && (
            <section className="internal-pages-section">
              <h2 className="section-title">{t('home.internalPages.title')}</h2>
              <div className="internal-pages-grid">
                {internalPageKeys.map((pageKey) => {
                  const pageInfo = internalPages[pageKey]
                  const slug = pageSlugs[pageKey]
                  
                  if (!pageInfo || !slug) return null
                  
                  return (
                    <Link
                      key={pageKey}
                      to={`/${slug}`}
                      className="internal-page-card"
                    >
                      {pageInfo.icon && (
                        <div className="internal-page-icon">{pageInfo.icon}</div>
                      )}
                      <h3 className="internal-page-title">{pageInfo.title}</h3>
                      {pageInfo.description && (
                        <p className="internal-page-description">{pageInfo.description}</p>
                      )}
                    </Link>
                  )
                })}
              </div>
            </section>
          )}

          <section className="cities-section">
            <h2 className="section-title">{t('home.popularCities')}</h2>
            <div className="cities-grid">
              {popularCities.map((city) => (
                <div
                  key={city.Slug}
                  className="city-card"
                  onClick={() => handleCityClick(city.Slug)}
                >
                  <h3 className="city-name">{city.City}</h3>
                  <p className="city-population">
                    {t('common.population')}: {city.Population.toLocaleString('de-DE')}
                  </p>
                </div>
              ))}
            </div>
          </section>

          <section className="cities-section">
            <h2 className="section-title">{t('home.allCities')}</h2>
            <div className="cities-grid">
              {cities.map((city) => (
                <div
                  key={city.Slug}
                  className="city-card"
                  onClick={() => handleCityClick(city.Slug)}
                >
                  <h3 className="city-name">{city.City}</h3>
                  <p className="city-population">
                    {t('common.population')}: {city.Population.toLocaleString('de-DE')}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ Section */}
          {faq && faq.items && faq.items.length > 0 && (
            <section className="faq-section">
              <div className="faq-container">
                <h2 className="faq-title">{faq.title || 'Frequently Asked Questions'}</h2>
                {faq.subtitle && <p className="faq-subtitle">{faq.subtitle}</p>}
                
                <div className="faq-list">
                  {faq.items?.map((item, index) => (
                    <div key={index} className={`faq-accordion-item ${openFaqIndex === index ? 'open' : ''}`}>
                      <button 
                        className="faq-accordion-header"
                        onClick={() => toggleFaq(index)}
                        aria-expanded={openFaqIndex === index}
                      >
                        <h3 className="faq-question">{item.question}</h3>
                        <span className="faq-accordion-icon">{openFaqIndex === index ? '−' : '+'}</span>
                      </button>
                      {openFaqIndex === index && (
                        <div className="faq-accordion-content">
                          <div className="faq-answer" dangerouslySetInnerHTML={{ __html: markdownToHtml(item.answer) }} />
                        </div>
                      )}
                    </div>
                  )) || []}
                </div>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  )
}

export default Home
